/**
 * Pure helpers shared by the two sides of the text-entry Office dialog: the
 * PARENT (`termEntryDialog.ts`, runs in the task pane / shared runtime) and the
 * DIALOG PAGE (`src/termDialog.ts`, runs in the dialog window).
 *
 * They live in their own module because the dialog page calls `Office.onReady`
 * at import time — anything importing that module would need a live Office
 * global, which is exactly what makes such code untestable. Nothing here touches
 * Office at all.
 */

export interface TextDialogPrompt {
  /** Window title. */
  title: string;
  /** Label above the input, e.g. "Term" / "Placeholder text:". */
  label: string;
  /** Input placeholder, e.g. "e.g. Agreement". */
  placeholder?: string;
  /** Small explanatory line under the input. */
  hint?: string;
}

export const DEFAULT_PROMPT: TextDialogPrompt = {
  title: 'Add defined term',
  label: 'Term',
  placeholder: 'e.g. Agreement',
  hint: 'Inserted as a quoted, bold defined term with a hidden XE index entry.',
};

/**
 * Builds the dialog URL, carrying the prompt wording as query parameters so one
 * page serves every caller (issue #19). A malformed URL here shows up as a dead
 * dialog in Word, which is expensive to diagnose live — hence pure and tested.
 */
export function buildDialogUrl(origin: string, prompt: TextDialogPrompt): string {
  const params = new URLSearchParams();
  params.set('title', prompt.title);
  params.set('label', prompt.label);
  if (prompt.placeholder) params.set('placeholder', prompt.placeholder);
  if (prompt.hint) params.set('hint', prompt.hint);
  return `${origin.replace(/\/+$/, '')}/term-dialog.html?${params.toString()}`;
}

/** The wording carried by a query string; absent parts stay undefined. */
export function readDialogParams(search: string): Partial<TextDialogPrompt> {
  const p = new URLSearchParams(search);
  const get = (k: string): string | undefined => {
    const v = p.get(k);
    return v && v.trim() ? v : undefined;
  };
  return {
    title: get('title'),
    label: get('label'),
    placeholder: get('placeholder'),
    hint: get('hint'),
  };
}

// ── Multi-field prompts ────────────────────────────────────────────────────
// "Insert cross-reference" needs one field, "Insert exhibit" two with
// prefilled defaults. Rather than a second dialog page, the whole form is
// described by the caller and rendered by the one page.

export interface DialogField {
  /** Key this field's value appears under in the result. */
  name: string;
  label: string;
  placeholder?: string;
  /** Prefilled value (e.g. "Exhibit", "[...]"). */
  value?: string;
  /** Blocks submit while empty. Default true. */
  required?: boolean;
}

export interface FieldsDialogSpec {
  title: string;
  fields: DialogField[];
  hint?: string;
}

/**
 * URL for a multi-field prompt: the spec travels as ONE json parameter.
 *
 * `title` is also sent flat, so a stale cached page that predates multi-field
 * support still shows a correctly-titled single input instead of asking for a
 * defined term — wrong, but recognisable, and the parent detects the
 * single-value reply and falls back to the pane form.
 */
export function buildFieldsDialogUrl(origin: string, spec: FieldsDialogSpec): string {
  const params = new URLSearchParams();
  params.set('spec', JSON.stringify(spec));
  params.set('title', spec.title);
  if (spec.fields[0]) {
    params.set('label', spec.fields[0].label);
    if (spec.fields[0].placeholder) params.set('placeholder', spec.fields[0].placeholder);
  }
  if (spec.hint) params.set('hint', spec.hint);
  return `${origin.replace(/\/+$/, '')}/term-dialog.html?${params.toString()}`;
}

/**
 * Parses the `spec` parameter. Returns null when absent OR malformed — the page
 * then renders its single-input form, which is the safe degradation.
 */
export function readDialogSpec(search: string): FieldsDialogSpec | null {
  const raw = new URLSearchParams(search).get('spec');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FieldsDialogSpec;
    if (!parsed || typeof parsed.title !== 'string' || !Array.isArray(parsed.fields)) return null;
    const fields = parsed.fields.filter(
      (f) => f && typeof f.name === 'string' && f.name && typeof f.label === 'string',
    );
    return fields.length ? { ...parsed, fields } : null;
  } catch {
    return null;
  }
}

/**
 * Reads a multi-field submit. Falls back to the single-value keys so one code
 * path handles both a current page and a stale one; the caller decides whether
 * the field it needed is present.
 */
export function readDialogValues(msg: {
  type?: string;
  values?: Record<string, string>;
  value?: string;
  term?: string;
}): Record<string, string> | null {
  if (msg.type !== 'submit') return null;
  if (msg.values && typeof msg.values === 'object') {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(msg.values)) {
      if (typeof v === 'string' && v.trim()) out[k] = v.trim();
    }
    return Object.keys(out).length ? out : null;
  }
  const single = readDialogValue(msg);
  return single ? { value: single } : null;
}

/**
 * Reads the entered value out of a dialog message.
 *
 * Accepts BOTH `value` (current) and `term` (the original key) because Word
 * caches dialog pages aggressively: a cached older term-dialog.html can talk to
 * newer parent code for a while after a deploy, and vice versa. Tolerating both
 * costs nothing and avoids a silently dead dialog across that skew.
 */
export function readDialogValue(msg: {
  type?: string;
  value?: string;
  term?: string;
}): string | null {
  if (msg.type !== 'submit') return null;
  const raw = (msg.value ?? msg.term ?? '').trim();
  return raw ? raw : null;
}
